import { useEffect, useMemo, useState } from "react";
import { Spin, message } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  useGetCasinoListQuery,
  useGetLiveScoresBatchQuery,
  useGetMatchesBySportQuery,
} from "../../../store/service/clientBetService";
import { formatOdd, resolveCasinoImage } from "./clientUtils";
import { normalizeLiveScore } from "./ClientLiveScore";
import {
  ALL_SPORTS_MENU,
  FEATURED_EVENTS,
  LobbySportIcon,
  QUICK_TAB_ITEMS,
  SPORT_STRIP_ITEMS,
} from "./lobbySportIcons";
import "./Client.scss";

const OddBox = ({ value, tone }) => {
  const n = Number(value);
  if (!(n > 0)) {
    return <span className={`w777h-odd is-empty ${tone || ""}`}>—</span>;
  }
  return <span className={`w777h-odd ${tone || ""}`}>{formatOdd(value)}</span>;
};

const MatchCard = ({ match, onOpen, liveScore }) => {
  const hasDraw = Number(match.drawBack) > 0 || Number(match.drawLay) > 0;
  const suspended = match.oddsSuspended || !match.hasOdds;
  const when = moment(match.openDate);
  const dayLabel = when.isSame(moment(), "day")
    ? "Today"
    : when.isSame(moment().add(1, "day"), "day")
      ? "Tomorrow"
      : when.format("DD MMM");
  const score = liveScore ? normalizeLiveScore({ data: liveScore.data ?? liveScore }) : null;

  return (
    <article
      className={`w777h-match ${match.inPlay ? "is-live" : ""}`}
      onClick={() => onOpen(match.matchId)}
      onKeyDown={(e) => e.key === "Enter" && onOpen(match.matchId)}
      role="button"
      tabIndex={0}>
      <div className="w777h-match-top">
        <div className="w777h-match-when">
          {match.inPlay ? (
            <>
              <span className="w777h-live-pill">LIVE</span>
              <strong>{when.format("HH:mm")}</strong>
            </>
          ) : (
            <>
              <span>{dayLabel}</span>
              <strong>{when.format("HH:mm")}</strong>
            </>
          )}
        </div>
        <div className="w777h-match-info">
          <h3>
            {match.matchName}
            {match.inPlay ? <em>Live</em> : null}
          </h3>
          <p>{match.league || "Cricket"}</p>
        </div>
      </div>

      {match.inPlay && score ? (
        <div className="w777h-live-score">
          <div className="w777h-live-score-team">
            <span>{score.team1.name}</span>
            <b>
              {score.team1.score} <small>({score.team1.overs})</small>
            </b>
          </div>
          <div className="w777h-live-score-team">
            <span>{score.team2.name}</span>
            <b>
              {score.team2.score} <small>({score.team2.overs})</small>
            </b>
          </div>
          {score.statusText ? <p className="w777h-live-score-status">{score.statusText}</p> : null}
        </div>
      ) : match.inPlay ? (
        <div className="w777h-live-score is-wait">
          {liveScore ? "Score feed pending — match open karo (TV score)" : "Live score loading…"}
        </div>
      ) : null}

      <div className={`w777h-odds-grid ${suspended ? "is-locked" : ""}`}>
        <OddBox value={match.team1Back} tone="back" />
        <OddBox value={match.team1Lay} tone="lay" />
        <OddBox value={hasDraw ? match.drawBack : null} tone="back" />
        <OddBox value={hasDraw ? match.drawLay : null} tone="lay" />
        <OddBox value={match.team2Back} tone="back" />
        <OddBox value={match.team2Lay} tone="lay" />
      </div>
    </article>
  );
};

const ClientHome = () => {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [mainTab, setMainTab] = useState("exchange");
  const [listFilter, setListFilter] = useState("all"); // all | live | upcoming
  const [activeSport, setActiveSport] = useState(SPORT_STRIP_ITEMS[0]);
  const sportId = activeSport?.sportId || 4;

  useEffect(() => {
    const sid = Number(searchParams.get("sport"));
    if (!sid) return;
    const fromStrip = SPORT_STRIP_ITEMS.find((s) => s.sportId === sid);
    const fromMenu = ALL_SPORTS_MENU.find((s) => s.sportId === sid);
    const label = searchParams.get("label") || fromStrip?.label || fromMenu?.label || "Sport";
    const icon = fromStrip?.icon || fromMenu?.icon || "cricket";
    setMainTab("exchange");
    setActiveSport(fromStrip || { label, icon, sportId: sid });
  }, [searchParams]);

  const { data, isLoading, isFetching, isError } = useGetMatchesBySportQuery(sportId, {
    pollingInterval: 3000,
    skip: mainTab === "casino" || !activeSport?.sportId,
    refetchOnMountOrArgChange: true,
  });

  const { data: cricketTicker } = useGetMatchesBySportQuery(4, {
    pollingInterval: 15000,
  });

  const { data: casinoData } = useGetCasinoListQuery(undefined, {
    pollingInterval: 30000,
  });

  const matches = useMemo(() => {
    const list = [...(data?.data || [])];
    list.sort(
      (a, b) => Number(b.inPlay) - Number(a.inPlay) || new Date(a.openDate) - new Date(b.openDate)
    );
    return list;
  }, [data]);

  const liveMatches = useMemo(() => matches.filter((m) => m.inPlay), [matches]);
  const upcomingMatches = useMemo(() => matches.filter((m) => !m.inPlay), [matches]);

  const visibleMatches = useMemo(() => {
    if (listFilter === "live") return liveMatches;
    if (listFilter === "upcoming") return upcomingMatches;
    return matches;
  }, [listFilter, liveMatches, upcomingMatches, matches]);

  const liveIds = useMemo(
    () => liveMatches.map((m) => m.matchId).filter(Boolean).slice(0, 10),
    [liveMatches]
  );

  const { data: scoresBatch } = useGetLiveScoresBatchQuery(liveIds, {
    skip: liveIds.length === 0 || mainTab === "casino",
    pollingInterval: 8000,
  });

  const scoreMap = scoresBatch?.data || {};

  const tickerMatches = cricketTicker?.data || [];
  const casinoGames = casinoData?.data || [];

  const openMatch = (id) => nav(`/play/match/${id}`);
  const openCasino = (game) => {
    const id = game?.eventId || game?.tableId || game?.id;
    if (!id) {
      message.info("Table id missing");
      return;
    }
    nav(`/play/casino/${id}`);
  };

  return (
    <div className="w777h-home">
      <div className="w777h-featured">
        {(tickerMatches.length
          ? tickerMatches.slice(0, 6).map((m) => ({
              label: m.matchName,
              icon: "cricket",
              tone: m.inPlay ? "sport-live" : "sport-cricket",
              id: m.matchId,
              live: m.inPlay,
            }))
          : FEATURED_EVENTS.map((e, i) => ({ ...e, id: `f-${i}` }))
        ).map((event) => (
          <button
            key={event.id || event.label}
            type="button"
            className={`w777h-featured-chip ${event.tone || ""} ${event.live ? "is-live" : ""}`}
            onClick={() => (event.id && String(event.id).match(/^\d/) ? openMatch(event.id) : null)}>
            <LobbySportIcon name={event.icon || "cricket"} className="w777-featured-icon" />
            <span>
              {event.live ? "● " : ""}
              {event.label}
            </span>
          </button>
        ))}
      </div>

      <div className="w777h-activity-label">
        Current Activities <span>▾</span>
      </div>

      <div className="w777h-quick-tabs">
        {QUICK_TAB_ITEMS.map((tab) => {
          const id =
            tab.label === "Exchange"
              ? "exchange"
              : tab.label === "Live Casino"
                ? "casino"
                : tab.label.toLowerCase().replace(/\s+/g, "-");
          return (
            <button
              key={tab.label}
              type="button"
              className={mainTab === id || (mainTab === "exchange" && tab.label === "Exchange") ? "active" : ""}
              onClick={() => {
                if (id === "casino") setMainTab("casino");
                else if (tab.label === "Exchange") setMainTab("exchange");
                else message.info(`${tab.label} jaldi aa raha hai`);
              }}>
              <LobbySportIcon name={tab.icon} className="w777-tab-icon" />
              <span>{tab.label.replace("Sports ", "").replace("Live ", "")}</span>
            </button>
          );
        })}
      </div>

      {mainTab === "casino" ? (
        <section className="w777h-section">
          <h4>Our Casino</h4>
          <div className="w777h-casino-grid">
            {casinoGames.length === 0 ? (
              <div className="w777h-empty">Loading games…</div>
            ) : (
              casinoGames.map((game, i) => (
                <button
                  key={game?.tableId || game?.eventId || game?.id || i}
                  type="button"
                  className="w777h-casino-card"
                  onClick={() => openCasino(game)}>
                  <img src={resolveCasinoImage(game.name, i)} alt={game.name} loading="lazy" />
                  <span>{game.name}</span>
                </button>
              ))
            )}
          </div>
        </section>
      ) : (
        <>
          <div className="w777h-sports">
            {SPORT_STRIP_ITEMS.map((sport) => (
              <button
                key={sport.label}
                type="button"
                className={activeSport.label === sport.label ? "active" : ""}
                onClick={() => {
                  setActiveSport(sport);
                  if (!sport.sportId) message.info(`${sport.label} jaldi`);
                }}>
                <LobbySportIcon name={sport.icon} className="w777-sport-icon" />
                <span>{sport.label}</span>
              </button>
            ))}
          </div>

          <div className="w777h-list-filters">
            <button
              type="button"
              className={listFilter === "all" ? "active" : ""}
              onClick={() => setListFilter("all")}>
              All ({matches.length})
            </button>
            <button
              type="button"
              className={listFilter === "live" ? "active" : ""}
              onClick={() => setListFilter("live")}>
              Live ({liveMatches.length})
            </button>
            <button
              type="button"
              className={listFilter === "upcoming" ? "active" : ""}
              onClick={() => setListFilter("upcoming")}>
              Upcoming ({upcomingMatches.length})
            </button>
          </div>

          <div className="w777h-count">
            {isError
              ? "Match feed error — retrying…"
              : isFetching
                ? "Refreshing…"
                : `${visibleMatches.length} matches`}
          </div>

          {(isLoading || isFetching) && visibleMatches.length === 0 ? (
            <div className="w777h-empty">
              <Spin tip="Loading matches…" />
            </div>
          ) : visibleMatches.length === 0 ? (
            <div className="w777h-empty">
              {listFilter === "live"
                ? "Abhi koi live match nahi — start hone pe yahan dikhega"
                : /invalid sport/i.test(String(data?.providerMessage || data?.message || ""))
                  ? `${activeSport.label} feed abhi provider pe available nahi — naya API chahiye`
                  : "No matches right now"}
            </div>
          ) : (
            <div className="w777h-match-list">
              {visibleMatches.map((match) => (
                <MatchCard
                  key={match.matchId}
                  match={match}
                  onOpen={openMatch}
                  liveScore={scoreMap[String(match.matchId)]}
                />
              ))}
            </div>
          )}

          <section className="w777h-section">
            <h4>Our Casino</h4>
            <div className="w777h-casino-row">
              {casinoGames.slice(0, 6).map((game, i) => (
                <button
                  key={`c-${game?.tableId || i}`}
                  type="button"
                  className="w777h-casino-card"
                  onClick={() => openCasino(game)}>
                  <img src={resolveCasinoImage(game.name, i)} alt={game.name} loading="lazy" />
                  <span>{game.name}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="w777h-section">
            <h4>New Games</h4>
            <div className="w777h-casino-row">
              {casinoGames.slice(6, 12).map((game, i) => (
                <button
                  key={`n-${game?.tableId || i}`}
                  type="button"
                  className="w777h-casino-card"
                  onClick={() => openCasino(game)}>
                  <img src={resolveCasinoImage(game.name, i + 6)} alt={game.name} loading="lazy" />
                  <span>{game.name}</span>
                </button>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default ClientHome;
